/**
 * @file Badge.tsx
 * @brief Tiny pill-style badge for statuses, roles, counts.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 03-10-2026: no border on any variant (DES)
 */

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";

const badgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-brand-soft text-brand-soft-foreground",
        primary: "bg-primary text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground",
        destructive:
          "bg-destructive/15 text-destructive",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        info: "bg-info-soft text-info",
        muted: "bg-muted text-muted-foreground",
        // No outline any more (DES 03-10-2026: pills have no border, app
        // wide): a soft fill. The variant name stays for its callers.
        outline: "bg-muted text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// `badgeVariants` stays module-private: it had no consumer, and a value export
// beside a component costs the module its Fast Refresh. Callers should use
// <Badge variant="…"> rather than the cva recipe. (02-08-2026)
